import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Category } from '../../components/products-page/components/products-categories/models/Category.model';
import { useEffect, useState } from 'react';
import SecondLevelProductCard from '../../components/second-level-product-card/SecondLevelProductCard';
import ProductFiltersMenu from '../../components/product-filters-menu/ProductFiltersMenu';
import { getFixcenterCategories, getTranslateNameByProductUuid } from '../../services/products.service';
import { FixcenterFilterDto } from '../../components/product-filters-menu/models/fixcenter-filter-dto.model';
import { ProductFilter } from '../../components/product-filters-menu/models/product-filter.model';
import ProductsSkeletonGrid from '../../components/second-level-product-card/product-skeleton/product-skeleton';
import { Utils } from '../../shared/constants/utilities.constants';
import { useTranslation } from '../../i18n/useTranslation';

const FILTERS_WIDTH = 340;
const CARD_MIN_WIDTH = 260;
const CONTENT_PADDING_X = 40;

export default function ProductsSecondLevel() {
    const { t } = useTranslation('translation');
    const { categoryId, directoryId, categoryName } = useParams();

    const [products, setProducts] = useState<Category[]>([]);
    const [searchByName, setSearchByName] = useState<string>('');
    const [isAnimationVisible, setIsAnimationVisible] = useState<boolean>(false);
    const [translateCategoryName, setTranslateCategoryName] = useState<string | null>(null);
    const [breadcrumbsTranslationCodes, setBreadcrumbsTranslationCodes] = useState<Record<string, string>>();
    const handleCategoryNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchByName(event.target.value);
    };

    const fetchCategories = async (fixcenterFilter: FixcenterFilterDto) => {
        try {
            if (categoryId) {
                setIsAnimationVisible(true);
                getFixcenterCategories(fixcenterFilter, directoryId).then((response: any) => {
                    if (response) {
                        const categoriesResponse = response.data as Category[];
                        setProducts(categoriesResponse);
                        setIsAnimationVisible(false);
                    }
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchTranslateNameByCategoryUuid = async () => {
        if (categoryId) {
            const languageCode = localStorage.getItem('lang');
            getTranslateNameByProductUuid(categoryId, languageCode).then((response: any) => {
                if (response) {
                    const translateName: string | null = response.data;
                    if (translateName && categoryName) {
                        setTranslateCategoryName(translateName);
                        setBreadcrumbsTranslationCodes(Utils.createRecord(categoryName, translateName));
                    }
                }
            });
        }
    };

    useEffect(() => {
        if (categoryId) {
            const languageCode = localStorage.getItem('lang');
            const productFilter: ProductFilter = {
                isCategory: true,
                parentProductId: categoryId,
            };
            const fixcenterFilter: FixcenterFilterDto = {
                productFilter: productFilter,
            };
            if (languageCode) {
                fixcenterFilter.languageCode = languageCode;
            }
            fetchCategories(fixcenterFilter);
            fetchTranslateNameByCategoryUuid();
        }
    }, [categoryId]);

    const contentSx = {
        flex: '1 1 0',
        minWidth: CARD_MIN_WIDTH + 2 * CONTENT_PADDING_X,
        py: '5%',
        px: `${CONTENT_PADDING_X}px`,
    };

    // AppContainer (the page scroller) has overflow-x: hidden. Enable the horizontal scroll for the whole page
    // only while this view is mounted, so other pages are unaffected.
    useEffect(() => {
        const appContainer = document.getElementById('AppContainer');
        if (!appContainer) {
            return;
        }
        const previousOverflowX = appContainer.style.overflowX;
        appContainer.style.overflowX = 'auto';
        return () => {
            appContainer.style.overflowX = previousOverflowX;
        };
    }, []);

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    // Smallest layout: filters + one card per row. Below this the whole page scrolls horizontally.
                    minWidth: FILTERS_WIDTH + CARD_MIN_WIDTH + 2 * CONTENT_PADDING_X,
                }}
            >
                <Box
                    sx={{
                        flex: `0 0 ${FILTERS_WIDTH}px`,
                        width: FILTERS_WIDTH,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                    }}
                >
                    <ProductFiltersMenu
                        categoryName={categoryName}
                        fetchCategories={fetchCategories}
                        searchByName={handleCategoryNameChange}
                        breadcrumbsTranslationCodes={breadcrumbsTranslationCodes}
                    />
                </Box>
                {isAnimationVisible ? (
                    <Box component="section" sx={contentSx}>
                        <ProductsSkeletonGrid count={20}></ProductsSkeletonGrid>
                    </Box>
                ) : (
                    <Box sx={contentSx}>
                        <Box>
                            {products.length > 0 ? (
                                <Typography sx={{ fontSize: 42, fontWeight: 600, color: '#821818' }}>
                                    {translateCategoryName && translateCategoryName.length > 0
                                        ? translateCategoryName
                                        : categoryName}
                                </Typography>
                            ) : (
                                <Typography sx={{ fontSize: 30, fontWeight: 600, color: '#333333' }}>
                                    {t('NOTIFICATION')}
                                    <br></br>
                                    {t('NO_PRODUCTS_MATCH_FILTERS')}
                                </Typography>
                            )}
                        </Box>
                        {categoryName && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    // As many columns as fit (at least CARD_MIN_WIDTH each), growing and shrinking with the width.
                                    gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
                                }}
                            >
                                {products.map((product, index) => (
                                    <Box key={index} sx={{ minWidth: 0, minHeight: 250, mb: '40px' }}>
                                        <SecondLevelProductCard categoryName={categoryName} product={product} />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
